import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, CalendarCheck, Inbox } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, errText } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";

const STATUSES = ["new", "contacted", "visited", "closed"];

export default function Enquiries() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enquiries");
      setRows(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => (r.status || "new") === filter)),
    [rows, filter]
  );

  const setStatus = async (enq, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${enq.enquiry_id}`, { status });
      setRows((list) => list.map((r) => (r.enquiry_id === enq.enquiry_id ? { ...r, ...data } : r)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-10" data-testid="enquiries-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl">My enquiries</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Everyone who enquired about your listings — call, WhatsApp or email them straight from here.
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-11 w-44 rounded-lg" data-testid="enquiry-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All enquiries</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-[12px] text-muted-foreground" data-testid="enquiry-counts">
        <span>{rows.length} total</span>
        {STATUSES.map((s) => (
          <span key={s}>· {rows.filter((r) => (r.status || "new") === s).length} {s}</span>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border p-10 text-center" data-testid="enquiries-empty">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-display text-xl">No enquiries yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Once buyers contact you about a listing, they'll show up here.
          </p>
          <Link to="/post-property" className="mt-4 inline-block text-sm font-bold underline">
            List a property
          </Link>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {visible.map((e) => (
            <div key={e.enquiry_id} className="rounded-2xl border border-border p-5 hover:border-foreground/25 transition-colors duration-200" data-testid={`enquiry-card-${e.enquiry_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-[17px]">{e.name}</p>
                  <p className="mt-0.5 text-[13px] text-muted-foreground">
                    {e.property_id ? (
                      <Link to={`/property/${e.property_id}`} className="underline">
                        {e.property_title || "Listing"}
                      </Link>
                    ) : (
                      "General enquiry"
                    )}
                    {e.property_city ? ` · ${e.property_city}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {e.source && (
                    <Badge variant="outline" className="rounded-full" data-testid={`enquiry-source-${e.enquiry_id}`}>
                      {e.source}
                    </Badge>
                  )}
                  {e.visited && (
                    <Badge variant="secondary" className="rounded-full">
                      <CalendarCheck className="mr-1 h-3 w-3" /> Site visit
                    </Badge>
                  )}
                  <Select value={e.status || "new"} onValueChange={(v) => setStatus(e, v)}>
                    <SelectTrigger className="h-9 w-36 rounded-lg capitalize" data-testid={`enquiry-status-${e.enquiry_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {e.message && <p className="mt-3 text-sm text-muted-foreground">{e.message}</p>}

              <div className="mt-4 flex flex-wrap items-center gap-2 text-[13px] font-semibold">
                <a href={`tel:${e.phone}`} onClick={() => trackEvent("call_lead", { event_category: "contact", enquiry_id: e.enquiry_id })} className="flex items-center gap-1.5 rounded-lg border border-border px-3.5 py-2 hover:border-foreground/30 transition-colors duration-200" data-testid={`enquiry-call-${e.enquiry_id}`}>
                  <Phone className="h-3.5 w-3.5" /> {e.phone}
                </a>
                <a href={`https://wa.me/91${(e.phone || "").replace(/\D/g, "").slice(-10)}`} onClick={() => trackEvent("whatsapp_lead", { event_category: "contact", enquiry_id: e.enquiry_id })} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-border px-3.5 py-2 hover:border-foreground/30 transition-colors duration-200" data-testid={`enquiry-whatsapp-${e.enquiry_id}`}>
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
                {e.email && (
                  <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 rounded-lg border border-border px-3.5 py-2 hover:border-foreground/30 transition-colors duration-200" data-testid={`enquiry-email-${e.enquiry_id}`}>
                    <Mail className="h-3.5 w-3.5" /> {e.email}
                  </a>
                )}
                <span className="text-[12px] font-normal text-muted-foreground">
                  {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
